import { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast as sonnerToast } from 'sonner';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import {
  offlineMutationKeys,
  executeAddItemsToTab,
  applyAddItemsOptimistically,
  type AddItemsToTabVariables,
} from '@/lib/offlineMutations';
import { queryKeys } from '@/lib/queryKeys';
import type { Organization } from '@/types';

export const useAddItemToTab = (currentOrganization: Organization | null) => {
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.id ?? '';
  const tabsKey = queryKeys.tabs(orgId);
  const productsKey = queryKeys.products(orgId);
  const ingredientsKey = queryKeys.ingredients(orgId);
  const { translator } = useContext(LanguageProviderContext);

  return useMutation({
    mutationKey: offlineMutationKeys.addItemsToTab,
    mutationFn: (variables: AddItemsToTabVariables) => executeAddItemsToTab(variables),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: tabsKey });
      await queryClient.cancelQueries({ queryKey: productsKey });
      await queryClient.cancelQueries({ queryKey: ingredientsKey });
      await queryClient.cancelQueries({ queryKey: queryKeys.externalOrders(orgId) });

      return applyAddItemsOptimistically(queryClient, variables);
    },
    onSuccess: (_result, variables) => {
      const productName = variables.items[0]?.product.name ?? '';

      sonnerToast.success(translator('Tabs.productAdded'), {
        description: `${productName} ${translator('Tabs.productAddedDesc')}`,
        duration: 2000,
      });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTabs) {
        queryClient.setQueryData(tabsKey, context.previousTabs);
      }
      if (context?.previousProducts) {
        queryClient.setQueryData(productsKey, context.previousProducts);
      }
      if (context?.previousExternalOrders) {
        queryClient.setQueryData(queryKeys.externalOrders(orgId), context.previousExternalOrders);
      }

      sonnerToast.error(translator('Errors.errorProduct'), {
        description: translator('Errors.errorProductDesc'),
        duration: 3000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tabsKey });
      queryClient.invalidateQueries({ queryKey: productsKey });
      queryClient.invalidateQueries({ queryKey: ingredientsKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.externalOrders(orgId) });
    },
  });
};
